package fr.manyverse.app.presentation.components.gm_screen

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.detectHorizontalDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.draggable
import androidx.compose.foundation.gestures.rememberDraggableState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.SkipNext
import androidx.compose.material3.Card
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import fr.manyverse.app.domain.model.GmTimer
import fr.manyverse.app.domain.model.Sound
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.presentation.components.common_tabs.ToolItem
import fr.manyverse.app.presentation.components.modules.gm_timer.GmTimerBubble
import fr.manyverse.app.presentation.components.modules.timer.formatCountdown
import fr.manyverse.app.presentation.components.soundboard.soundTagPalette
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.gm_inventory
import manyverse.shared.generated.resources.gm_side_panel_open
import manyverse.shared.generated.resources.gm_timers
import manyverse.shared.generated.resources.tools
import manyverse.shared.generated.resources.icon_treasure_chest
import manyverse.shared.generated.resources.next_track
import manyverse.shared.generated.resources.tag_calm
import manyverse.shared.generated.resources.tag_combat
import manyverse.shared.generated.resources.tag_tension
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

private const val PANEL_WIDTH_FRACTION = 0.8f
private const val PANEL_MAX_WIDTH_DP = 600
private const val EDGE_SWIPE_WIDTH_DP = 24
private const val OPEN_DRAG_THRESHOLD_PX = 60f

/**
 * Volet latéral MJ glissant depuis la gauche, recouvrant 80% de la largeur.
 *
 * Rendu dans l'arbre de l'écran (et non dans des Popup/Dialog) : sa position d'affichage par rapport
 * aux autres overlays (tableau blanc, combat…) suit l'ordre de déclaration dans GameMasterScreen.
 * Les formulaires et bottom sheets, qui restent des Dialog, passent au-dessus.
 *
 * Fermé : seules la poignée et la zone de swipe (toutes deux sur le bord gauche) sont rendues — le
 * reste du toucher passe au contenu en dessous.
 * Ouvert : le volet et son scrim couvrent l'écran ; le scrim ferme le volet au tap.
 *
 * Réutilise [GmTimerBubble] avec `onTick = {}` pour afficher les timers MJ sans relancer leur
 * décompte (piloté par les bulles flottantes du GmBubbleOverlays).
 */
@Composable
fun GmSidePanel(
    isOpen: Boolean,
    onOpenChange: (Boolean) -> Unit,
    gmTimers: List<GmTimer>,
    onGmInventoryClick: () -> Unit,
    onTimerPause: (String) -> Unit,
    onTimerStop: (String) -> Unit,
    nowPlayingMusic: Sound?,
    isMusicPlaying: Boolean,
    positionMs: Long,
    durationMs: Long,
    showNextButton: Boolean,
    onMusicPlayPause: () -> Unit,
    onMusicNext: () -> Unit
) {
    if (isOpen) {
        GmSidePanelOpen(
            onClose = { onOpenChange(false) },
            gmTimers = gmTimers,
            onGmInventoryClick = onGmInventoryClick,
            onTimerPause = onTimerPause,
            onTimerStop = onTimerStop,
            nowPlayingMusic = nowPlayingMusic,
            isMusicPlaying = isMusicPlaying,
            positionMs = positionMs,
            durationMs = durationMs,
            showNextButton = showNextButton,
            onMusicPlayPause = onMusicPlayPause,
            onMusicNext = onMusicNext
        )
    } else {
        GmSidePanelHandle(onOpen = { onOpenChange(true) })
    }
}

@Composable
private fun GmSidePanelHandle(onOpen: () -> Unit) {
    // Conteneur sans gestion du toucher : seules ses deux zones l'interceptent
    Box(modifier = Modifier.fillMaxSize()) {
        // Zone de swipe invisible sur le bord gauche (ouverture naturelle du tiroir).
        // Déclarée avant la poignée, qui passe donc devant là où les deux zones se recouvrent.
        Box(
            modifier = Modifier
                .align(Alignment.CenterStart)
                .fillMaxHeight()
                .width(EDGE_SWIPE_WIDTH_DP.dp)
                .pointerInput(Unit) {
                    var total = 0f
                    detectHorizontalDragGestures(
                        onDragStart = { total = 0f },
                        onHorizontalDrag = { _, amount ->
                            total += amount
                            if (total > OPEN_DRAG_THRESHOLD_PX) onOpen()
                        }
                    )
                }
        )

        // Poignée visible sur le bord gauche, du même côté que l'ouverture du volet
        // (le bord droit est occupé par la barre d'onglets en paysage).
        Surface(
            color = MaterialTheme.colorScheme.primaryContainer,
            shape = RoundedCornerShape(topEnd = 12.dp, bottomEnd = 12.dp),
            shadowElevation = 4.dp,
            modifier = Modifier
                .align(Alignment.CenterStart)
                .height(56.dp)
                .clickable { onOpen() }
        ) {
            Box(
                modifier = Modifier.padding(vertical = 8.dp),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                    contentDescription = stringResource(Res.string.gm_side_panel_open),
                    tint = MaterialTheme.colorScheme.onPrimaryContainer
                )
            }
        }
    }
}

@Composable
private fun GmSidePanelOpen(
    onClose: () -> Unit,
    gmTimers: List<GmTimer>,
    onGmInventoryClick: () -> Unit,
    onTimerPause: (String) -> Unit,
    onTimerStop: (String) -> Unit,
    nowPlayingMusic: Sound?,
    isMusicPlaying: Boolean,
    positionMs: Long,
    durationMs: Long,
    showNextButton: Boolean,
    onMusicPlayPause: () -> Unit,
    onMusicNext: () -> Unit
) {
    // Le Dialog prenait le focus et fermait ainsi le clavier
    val focusManager = LocalFocusManager.current
    LaunchedEffect(Unit) { focusManager.clearFocus() }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val density = LocalDensity.current
        val maxWidthPx = with(density) { PANEL_MAX_WIDTH_DP.dp.toPx() }
        val panelWidthPx = (constraints.maxWidth * PANEL_WIDTH_FRACTION).coerceAtMost(maxWidthPx)
        val panelWidthDp = with(density) { panelWidthPx.toDp() }
        val scope = rememberCoroutineScope()

        // offsetX : -panelWidthPx (caché à gauche) .. 0f (ouvert)
        val offsetX = remember { Animatable(-panelWidthPx) }
        LaunchedEffect(panelWidthPx) { offsetX.updateBounds(-panelWidthPx, 0f) }
        LaunchedEffect(Unit) { offsetX.animateTo(0f) }

        val progress = ((offsetX.value + panelWidthPx) / panelWidthPx).coerceIn(0f, 1f)

        val closeWithAnim: suspend () -> Unit = {
            offsetX.animateTo(-panelWidthPx)
            onClose()
        }

        // Retour arrière (Android, Échap sur desktop, swipe de bord sur iOS) : ferme le volet
        // avec la même animation que le tap sur le scrim
        NavigationBackHandler(
            state = rememberNavigationEventState(NavigationEventInfo.None),
            onBackCompleted = { scope.launch { closeWithAnim() } }
        )

        // Scrim : la zone non recouverte (20% à droite) ferme le volet au clic.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.6f * progress))
                .pointerInput(Unit) {
                    detectTapGestures { scope.launch { closeWithAnim() } }
                }
        )

        val draggableState = rememberDraggableState { delta ->
            scope.launch { offsetX.snapTo(offsetX.value + delta) }
        }

        Surface(
            modifier = Modifier
                .align(Alignment.TopStart)
                .width(panelWidthDp)
                .fillMaxHeight()
                .offset { IntOffset(offsetX.value.roundToInt(), 0) }
                .draggable(
                    orientation = Orientation.Horizontal,
                    state = draggableState,
                    onDragStopped = {
                        if (offsetX.value > -panelWidthPx / 2f) offsetX.animateTo(0f)
                        else closeWithAnim()
                    }
                ),
            color = MaterialTheme.colorScheme.surfaceContainer,
            shadowElevation = 8.dp
        ) {
            GmSidePanelBody(
                gmTimers = gmTimers,
                onGmInventoryClick = onGmInventoryClick,
                onTimerPause = onTimerPause,
                onTimerStop = onTimerStop,
                nowPlayingMusic = nowPlayingMusic,
                isMusicPlaying = isMusicPlaying,
                positionMs = positionMs,
                durationMs = durationMs,
                showNextButton = showNextButton,
                onMusicPlayPause = onMusicPlayPause,
                onMusicNext = onMusicNext
            )
        }
    }
}

@Composable
private fun GmSidePanelBody(
    gmTimers: List<GmTimer>,
    onGmInventoryClick: () -> Unit,
    onTimerPause: (String) -> Unit,
    onTimerStop: (String) -> Unit,
    nowPlayingMusic: Sound?,
    isMusicPlaying: Boolean,
    positionMs: Long,
    durationMs: Long,
    showNextButton: Boolean,
    onMusicPlayPause: () -> Unit,
    onMusicNext: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            // Hors Dialog, le contenu n'est plus borné à la zone sûre
            .safeDrawingPadding()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // Widget musique (uniquement si une musique est chargée)
        if (nowPlayingMusic != null) {
            GmMusicWidget(
                sound = nowPlayingMusic,
                isPlaying = isMusicPlaying,
                positionMs = positionMs,
                durationMs = durationMs,
                showNextButton = showNextButton,
                onPlayPause = onMusicPlayPause,
                onNext = onMusicNext
            )
        }

        // Section Outils
        Card(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    text = stringResource(Res.string.tools),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    ToolItem(
                        painter = painterResource(Res.drawable.icon_treasure_chest),
                        label = stringResource(Res.string.gm_inventory),
                        onClick = onGmInventoryClick
                    )
                }
            }
        }

        // Section Timers MJ (uniquement s'il y en a en cours)
        if (gmTimers.isNotEmpty()) {
            Card(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = stringResource(Res.string.gm_timers),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold
                    )
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        gmTimers.forEach { timer ->
                            key(timer.id) {
                                GmTimerBubble(
                                    timer = timer,
                                    onTick = {},
                                    onPause = { onTimerPause(timer.id) },
                                    onStop = { onTimerStop(timer.id) }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun GmMusicWidget(
    sound: Sound,
    isPlaying: Boolean,
    positionMs: Long,
    durationMs: Long,
    showNextButton: Boolean,
    onPlayPause: () -> Unit,
    onNext: () -> Unit
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        tonalElevation = 2.dp
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // Infos sur 3 lignes : titre, tags, durée
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Text(
                    text = sound.name,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (sound.tags.isNotEmpty()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        sound.tags.forEach { tag ->
                            val palette = soundTagPalette(tag)
                            Box(
                                modifier = Modifier
                                    .background(palette.container, RoundedCornerShape(4.dp))
                                    .padding(horizontal = 6.dp, vertical = 2.dp)
                            ) {
                                Text(
                                    text = musicTagLabel(tag),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = palette.label
                                )
                            }
                        }
                    }
                }
                Text(
                    text = "${formatCountdown((positionMs / 1000).toInt())} / " +
                            formatCountdown((durationMs / 1000).toInt()),
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            // Contrôles empilés à droite
            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Bouton pause/play
                FilledIconButton(
                    onClick = onPlayPause,
                    modifier = Modifier.size(48.dp)
                ) {
                    Icon(
                        imageVector = if (isPlaying) Icons.Default.Pause else Icons.Default.PlayArrow,
                        contentDescription = if (isPlaying) "Pause" else "Play"
                    )
                }

                // Bouton titre suivant (mode aléatoire uniquement)
                if (showNextButton) {
                    FilledIconButton(
                        onClick = onNext,
                        modifier = Modifier.size(48.dp),
                        colors = IconButtonDefaults.filledIconButtonColors(
                            containerColor = MaterialTheme.colorScheme.surfaceVariant,
                            contentColor = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    ) {
                        Icon(
                            imageVector = Icons.Default.SkipNext,
                            contentDescription = stringResource(Res.string.next_track)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun musicTagLabel(tag: SoundTag): String = when (tag) {
    SoundTag.CALM -> stringResource(Res.string.tag_calm)
    SoundTag.COMBAT -> stringResource(Res.string.tag_combat)
    SoundTag.TENSION -> stringResource(Res.string.tag_tension)
}
